/**
 * GlobalDictionaryPicker — wybór GLOBALNEGO słownika (katalog ITE, `global_dictionaries.code`)
 * dla pola typu select/multi_select w edytorze szablonu Kreatora (hub, panel ITE).
 *
 * Renderuje `SearchableSelect` (wyszukiwarka po nazwie, opisie, kodzie i typie słownika)
 * zasilany `useGlobalDictionaries()` (Etap 3, lista słowników globalnych — patrz
 * `lib/global-dictionaries/api.ts`), ograniczony WYŁĄCZNIE do słowników ze statusem
 * `published` (szkice i wycofane nie nadają się jako źródło opcji dla nowo projektowanego
 * pola szablonu). Wartość = kod słownika, `null` gdy nic nie wybrano (pole korzysta z opcji
 * wpisanych ręcznie zamiast słownika) — czyszczenie przez „X" w polu. Opcje i plakietki typu
 * budują `dictionary-options.ts`.
 *
 * Odpowiednik `DictionaryPicker` (tenant, lista scalona lokalne+globalne) — ten
 * komponent działa w panelu ITE i widzi tylko katalog globalny.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/components/FormField';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useGlobalDictionaries } from '@/lib/global-dictionaries/api';

import { buildGlobalDictionaryOptions, withUnavailableValueOption } from './dictionary-options';

export interface GlobalDictionaryPickerProps {
  /** Kod wybranego słownika globalnego, `null` gdy brak (pole używa opcji wpisanych ręcznie). */
  value: string | null;
  onChange: (code: string | null) => void;
  disabled?: boolean;
  id?: string;
  'data-testid'?: string;
}

export function GlobalDictionaryPicker({
  value,
  onChange,
  disabled,
  id,
  'data-testid': testId = 'global-dictionary-picker',
}: GlobalDictionaryPickerProps) {
  const { t } = useTranslation();
  const { data: dictionaries, isLoading } = useGlobalDictionaries(false);

  const options = useMemo(() => {
    const built = buildGlobalDictionaryOptions(
      (dictionaries ?? []).filter((d) => d.status === 'published'),
      t,
    );
    // Opcja zastępcza „niedostępny" tylko dla listy, która faktycznie została pobrana — przy
    // ładowaniu i przy błędzie zapytania (`dictionaries === undefined`) nie wiemy, czy wartość
    // jest poza listą, więc nie oznaczamy jej jako niedostępnej.
    return dictionaries === undefined ? built : withUnavailableValueOption(built, value, t);
  }, [dictionaries, value, t]);

  return (
    <FormField label={t('common.globalDictionaryPicker.label')} htmlFor={id}>
      <SearchableSelect
        id={id}
        data-testid={testId}
        aria-label={t('common.globalDictionaryPicker.label')}
        disabled={disabled || isLoading}
        value={value}
        onChange={onChange}
        options={options}
        placeholder={t('common.globalDictionaryPicker.none')}
        allowClear
      />
    </FormField>
  );
}
